import type { AppRouter } from '@longy/api'
import { DotTranslationKey } from '@longy/common'
import { QueryClient } from '@tanstack/react-query'
import { createTRPCQueryUtils, createTRPCReact, httpLink, TRPCClientError } from '@trpc/react-query'
import { inferRouterOutputs } from '@trpc/server'
import i18n from 'i18next'
import superjson from 'superjson'

import { getJwt } from '../utils/jwt'

const getBackendUrl = () => {
    if (typeof window !== 'undefined') return import.meta.env.VITE_BACKEND_URL
    const env = globalThis.process?.env
    return env?.['VITE_BACKEND_URL'] || import.meta.env.VITE_BACKEND_URL
}

export type ClinicRouterOutput = inferRouterOutputs<AppRouter>

export const api = createTRPCReact<AppRouter>()

export const isTRPCClientError = (error: unknown): error is TRPCClientError<AppRouter> => {
    return error instanceof TRPCClientError
}

export const createTRPCClient = () => {
    return api.createClient({
        links: [
            httpLink({
                transformer: superjson,
                url: `${getBackendUrl()}/trpc`,
                headers() {
                    const token = getJwt()
                    return {
                        ...(token && { Authorization: `Bearer ${token}` }),
                        'x-client-version': '2.0.0',
                        'x-client-platform': 'web',
                        'x-locale': i18n.language,
                    }
                },
            }),
        ],
    })
}

export const createApiUtils = (opts: {
    queryClient: QueryClient
    client: ReturnType<typeof createTRPCClient>
}) => createTRPCQueryUtils<AppRouter>(opts)

/**
 * The live QueryClient is the per-request one built in `router.tsx`, so this type is derived from
 * the utils shape rather than from a module-level instance. There used to be a second client and a
 * `languageChanged` listener here; nothing rendered from them, so switching language invalidated a
 * cache no component read.
 */
export type ApiUtils = ReturnType<typeof createApiUtils>

export const getTRPCErrorMessage = (error: unknown) => {
    if (isTRPCClientError(error) && error.data?.code !== 'INTERNAL_SERVER_ERROR') {
        const errorMessage = error.message
        const isTranslated = i18n.exists(errorMessage)
        return isTranslated ? i18n.t(errorMessage as DotTranslationKey) : errorMessage
    }
    return i18n.t('common.errors.defaultError')
}
